import React, { useState, useEffect, useContext, useRef } from 'react';
import { Button } from '@tranquility-ai/theme-components/button';
import { Menu, MenuItem, MenuSection } from '@tranquility-ai/theme-components/menu';
import {
  PlayIcon, PauseIcon, SpeakerHighIcon, SpeakerXIcon, SubtitlesIcon, SubtitlesSlashIcon, ChatCircleSlashIcon,
  SquareHalfIcon, SquareHalfBottomIcon, GearIcon, ChatCircleIcon, CaretDownIcon,   CameraIcon, CameraSlashIcon, StopIcon,
  QueueIcon,
  FilmReelIcon,
} from '@phosphor-icons/react';
import { FeaturesContext, StatsContext } from '@/app/contexts';
import { recordStat, formatTimeIndex } from '@/shared/functions';
import { MediaSource } from '@/shared/types';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { mediaIconButtonProps } from './media-icon-button';
// TODO: store times in mediaContext to use for "displayPosition", and start/end clip #t=17.79,25.12

/**
 * Width thresholds for the controls bar, measured on the bar itself (not the
 * viewport) so timeline cards and side panels lay out from their own box.
 *
 * - below `STACK`: volume / speed / settings drop to a second row.
 * - below `MINIMAL`: a second row cannot fit either, so secondary controls are
 *   dropped entirely and only play + timestamp remain.
 */
/** Keep in sync with ``AUDIO_SIDE_PLAYER_MIN_WIDTH_PX`` in below-stack-layout.ts. */
const MEDIA_CONTROLS_STACK_WIDTH_PX = 340;
const MEDIA_CONTROLS_MINIMAL_WIDTH_PX = 260;

interface ControlsProps {
  isPlaying: boolean;
  togglePlay: () => void;
  isMuted: boolean;
  toggleMute: () => void;
  volume: number;
  handleVolumeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  currentTime: string;
  duration: number;
  isMenuVisible: boolean;
  toggleMenuVisibility: () => void;
  playbackRate: number;
  handlePlaybackRateChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  showTranscript: boolean;
  toggleTranscriptVisibility: () => void;
  showTranscriptMarkers: boolean;
  toggleTranscriptMarkersVisibility: () => void;
  transcriptAvailable: boolean;
  transcriptText?: string;
  transcriptSrc?: string;
  menuRef: React.RefObject<HTMLDivElement>;
  changeTranscriptPosition: ((position: 'side' | 'below') => void) | null;
  /** Concrete resolved layout actually being rendered right now — drives the
   *  single toggle item's label/icon and which position clicking it switches to. */
  currentPosition: 'side' | 'below';
  setStillFrame: () => void;
  clearStillFrame: () => void;
  hasStillFrameImage: boolean;
  setStartTime: () => void;
  setEndTime: () => void;
  mediaItem: MediaSource;
  autoScroll: boolean;
  toggleAutoScroll: () => void;
  updateMediaContext: (key: string, value: any) => void;
  setIsEditingMediaContext: (isEditingMediaContext: boolean) => void;
  isEditingMediaContext: boolean;
  isEditing: boolean;
  /** Compact layout for horizontal timeline cards — controls only, no scrubber or settings menu. */
  compact?: boolean;
  /** Seek the element to restored trim times (cancel / restore originals). */
  onRestoreTrimTimes?: (startTime: unknown, endTime?: unknown) => void;
  /** Capture mode: disable transport so a drag cannot play/seek/mute. */
  disabled?: boolean;
}

const Controls: React.FC<ControlsProps> = ({
  isPlaying,
  togglePlay,
  isMuted,
  toggleMute,
  volume,
  handleVolumeChange,
  currentTime,
  duration,
  isMenuVisible,
  toggleMenuVisibility,
  playbackRate,
  handlePlaybackRateChange,
  showTranscript,
  toggleTranscriptVisibility,
  showTranscriptMarkers,
  toggleTranscriptMarkersVisibility,
  transcriptAvailable,
  transcriptText,
  transcriptSrc,
  menuRef,
  changeTranscriptPosition,
  currentPosition,
  setStillFrame,
  clearStillFrame,
  hasStillFrameImage,
  setStartTime,
  setEndTime,
  mediaItem,
  autoScroll,
  toggleAutoScroll,
  updateMediaContext,
  setIsEditingMediaContext,
  isEditingMediaContext,
  isEditing,
  compact = false,
  onRestoreTrimTimes,
  disabled = false,
}) => {
  const t = useTimePilotT();
  const statsContext = useContext(StatsContext);
  const { featureFlags } = useContext(FeaturesContext) || {};
  const controlsRef = useRef<HTMLDivElement>(null);
  const [barWidth, setBarWidth] = useState<number | null>(null);
  const [isMenuVisibleState, setIsMenuVisible] = useState(false);
  const [showTranscriptMarkersState, setShowTranscriptMarkersState] = useState(false);
  const [playbackRateMenuOpen, setPlaybackRateMenuOpen] = useState(false);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  /** Until measured, assume a full-width bar so wide players never flash stacked. */
  const minimal = barWidth != null && barWidth < MEDIA_CONTROLS_MINIMAL_WIDTH_PX;
  const stacked =
    !minimal && barWidth != null && barWidth < MEDIA_CONTROLS_STACK_WIDTH_PX;

  useEffect(() => {
    const node = controlsRef.current;
    if (!node) return;

    setBarWidth(node.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setBarWidth(entry.contentRect.width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setShowTranscriptMarkersState(showTranscriptMarkers);
  }, [showTranscriptMarkers]);

  const handleToggleMenuVisibility = () => {
    setIsMenuVisible(!isMenuVisibleState);
    toggleMenuVisibility();
  };

  const handleToggleTranscriptMarkers = () => {
    setShowTranscriptMarkersState(!showTranscriptMarkersState);
    toggleTranscriptMarkersVisibility();
  };

  useEffect(() => {
    if (disabled) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore hotkeys if any input element or markdown editor has focus
      if (event.target instanceof HTMLInputElement || 
          event.target instanceof HTMLTextAreaElement ||
          event.target instanceof HTMLSelectElement ||
          (event.target instanceof HTMLElement && event.target.closest('.mdxeditor-root-contenteditable')) ||
          (event.target instanceof HTMLElement && event.target.closest('#feedback-button'))) {
        return;
      }

      if (event.key === 't') {
        toggleTranscriptVisibility();
      } else if (event.key === 'm') {
        handleToggleTranscriptMarkers();
      } else if (event.key === 'p' && !!changeTranscriptPosition) {
        changeTranscriptPosition(currentPosition === 'side' ? 'below' : 'side');
      } else if (event.key === 'a') {
        toggleAutoScroll();
      } else if (event.key === 'e') {
        // Toggle edit mode
        if (updateMediaContext && !isEditingMediaContext) {
          // Initialize mediaContext if it doesn't exist
          if (!mediaItem.mediaContext) {
            updateMediaContext('mediaContext', {});
          }
          
          // Store original times when entering edit mode
          const originalStartTime = mediaItem.mediaContext?.startTime !== undefined 
            ? mediaItem.mediaContext.startTime 
            : mediaItem.mediaContext?.sourceContext?.startTime || 0;
          
          const originalEndTime = mediaItem.mediaContext?.endTime !== undefined
            ? mediaItem.mediaContext.endTime
            : duration;

          updateMediaContext('originalStartTime', originalStartTime);
          updateMediaContext('originalEndTime', originalEndTime);
          
          // Initialize new start and end times
          // If there are existing mediaContext times, use those; otherwise start fresh
          updateMediaContext('startTime', mediaItem.mediaContext?.startTime !== undefined 
            ? mediaItem.mediaContext.startTime 
            : 0);
          updateMediaContext('endTime', mediaItem.mediaContext?.endTime !== undefined
            ? mediaItem.mediaContext.endTime
            : duration);
        } else {
          setIsEditingMediaContext(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [disabled, currentPosition, toggleTranscriptVisibility, handleToggleTranscriptMarkers, changeTranscriptPosition, toggleAutoScroll, updateMediaContext, mediaItem.mediaContext?.startTime, mediaItem.mediaContext?.endTime, isEditingMediaContext, duration]);

  const handleSetStillFrame = () => {
    setStillFrame();
  };

  const handleClearStillFrame = () => {
    clearStillFrame();
  };

  const handleSetStartTime = () => {
    setStartTime();
  };

  const handleSetEndTime = () => {
    setEndTime();
  };

  const handleSave = () => {
    if (updateMediaContext) {
      // Convert to numbers before saving if they're strings (allow 0)
      if (mediaItem.mediaContext?.startTime != null && mediaItem.mediaContext.startTime !== '') {
        updateMediaContext('startTime', Number(mediaItem.mediaContext.startTime));
      }
      if (mediaItem.mediaContext?.endTime != null && mediaItem.mediaContext.endTime !== '') {
        updateMediaContext('endTime', Number(mediaItem.mediaContext.endTime));
      }
      setIsEditingMediaContext(false);
    }
  };

  const handleCancel = () => {
    if (updateMediaContext) {
      const originalStart = mediaItem.mediaContext?.originalStartTime ?? 0;
      const originalEnd = mediaItem.mediaContext?.originalEndTime ?? duration;
      updateMediaContext('startTime', originalStart);
      updateMediaContext('endTime', originalEnd);
      setIsEditingMediaContext(false);
      onRestoreTrimTimes?.(originalStart, originalEnd);
    }
  };

  const handleEditModeToggle = () => {
    if (updateMediaContext) {
      if (!isEditingMediaContext) {
        // Initialize mediaContext if it doesn't exist
        if (!mediaItem.mediaContext) {
          updateMediaContext('mediaContext', {});
        }
        
        // Store original times when entering edit mode
        const originalStartTime = mediaItem.mediaContext?.startTime !== undefined 
          ? mediaItem.mediaContext.startTime 
          : mediaItem.mediaContext?.sourceContext?.startTime || 0;
        
        const originalEndTime = mediaItem.mediaContext?.endTime !== undefined
          ? mediaItem.mediaContext.endTime
          : duration;

        updateMediaContext('originalStartTime', originalStartTime);
        updateMediaContext('originalEndTime', originalEndTime);
        
        // Initialize new start and end times
        // If there are existing mediaContext times, use those; otherwise start fresh
        updateMediaContext('startTime', mediaItem.mediaContext?.startTime !== undefined 
          ? mediaItem.mediaContext.startTime 
          : 0);
        updateMediaContext('endTime', mediaItem.mediaContext?.endTime !== undefined
          ? mediaItem.mediaContext.endTime
          : duration);
        setIsEditingMediaContext(true);
      } else {
        // Exiting edit mode
        setIsEditingMediaContext(false);
      }
    }
  };

  const handleRestoreOriginalTimes = () => {
    if (updateMediaContext && mediaItem.mediaContext?.originalStartTime !== undefined) {
      const originalStart = mediaItem.mediaContext.originalStartTime ?? 0;
      const originalEnd = mediaItem.mediaContext.originalEndTime ?? duration;
      updateMediaContext('startTime', originalStart);
      updateMediaContext('endTime', originalEnd);
      onRestoreTrimTimes?.(originalStart, originalEnd);
    }
  };

  const playbackRateDropdown = !isEditingMediaContext ? (
    <Menu
      isOpen={playbackRateMenuOpen}
      onOpenChange={setPlaybackRateMenuOpen}
      inverted
      placement="right"
      aria-label={t('media.playbackSpeed')}
      content={
        <>
          {['0.5', '1', '1.5', '2'].map((rate) => (
            <MenuItem
              key={rate}
              onSelect={() => {
                handlePlaybackRateChange({ target: { value: rate } } as React.ChangeEvent<HTMLSelectElement>);
                handleToggleMenuVisibility();
              }}
            >
              {rate}x
            </MenuItem>
          ))}
        </>
      }
    >
      <Button
        type="button"
        size="sm"
        style="outline"
        color="light"
        background="inverse"
        data-testid="playback-rate-trigger"
        aria-label={t('media.playbackSpeedValue', { rate: playbackRate })}
        aria-haspopup="menu"
        trailingIcon={<CaretDownIcon />}
        className="min-h-0 px-1.5 py-0.5 text-xs"
        onClick={() => setPlaybackRateMenuOpen(!playbackRateMenuOpen)}
      >
        {playbackRate}x
      </Button>
    </Menu>
  ) : null;

  const volumeControl = (
    <div className="flex shrink-0 items-center">
      <Button
        type="button"
        {...mediaIconButtonProps}
        aria-label={isMuted ? t('media.unmute') : t('media.mute')}
        disabled={disabled}
        onClick={toggleMute}
      >
        {isMuted ? <SpeakerXIcon weight="fill" size={20} /> : <SpeakerHighIcon weight="fill" size={20} />}
      </Button>
      <div
        className="relative flex h-6 w-16 shrink-0 items-center overflow-visible"
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-white/15"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute left-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-brand-blue-light"
          style={{ width: `calc(${isMuted ? 0 : volume * 100}%)` }}
          aria-hidden="true"
        />
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={isMuted ? 0 : volume}
          onChange={handleVolumeChange}
          disabled={disabled}
          aria-label={t('media.volume')}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round((isMuted ? 0 : volume) * 100)}
          className="absolute inset-0 z-10 h-full w-full cursor-pointer appearance-none bg-transparent outline-none volume-slider touch-none"
        />
      </div>
    </div>
  );

  const settingsMenu = !compact && (transcriptSrc || mediaItem.mediaType === 'video') ? (
    <Menu
      isOpen={settingsMenuOpen}
      onOpenChange={setSettingsMenuOpen}
      inverted
      placement="bottom-end"
      aria-label={t('media.settings')}
      content={
        <>
          {transcriptSrc ? (
            <MenuSection title={t('media.transcript')}>
              {transcriptAvailable ? (
                <>
                  <MenuItem
                    icon={!showTranscript ? <SubtitlesIcon size={20} /> : <SubtitlesSlashIcon size={20} />}
                    onSelect={toggleTranscriptVisibility}
                    shortcut="T"
                  >
                    {!showTranscript ? t('media.showTranscripts') : t('media.hideTranscripts')}
                  </MenuItem>
                  <MenuItem
                    disabled={!changeTranscriptPosition}
                    icon={!changeTranscriptPosition || currentPosition === 'side' ? <SquareHalfBottomIcon size={20} /> : <SquareHalfIcon size={20} />}
                    onSelect={() => {
                      changeTranscriptPosition?.(currentPosition === 'side' ? 'below' : 'side');
                    }}
                    shortcut="P"
                  >
                    {t(!changeTranscriptPosition || currentPosition === 'side' ? 'media.placeBelow' : 'media.placeBeside')}
                  </MenuItem>
                  <MenuItem icon={<QueueIcon size={20} />} onSelect={toggleAutoScroll} shortcut="A">
                    {t(autoScroll ? 'media.disableAutoScroll' : 'media.enableAutoScroll')}
                  </MenuItem>
                </>
              ) : (
                <MenuItem disabled icon={<SubtitlesSlashIcon size={20} />}>
                  {t('media.noTranscript')}
                </MenuItem>
              )}
            </MenuSection>
          ) : null}
          {transcriptSrc && transcriptAvailable ? (
            <MenuSection title={t('media.track')}>
              <MenuItem
                icon={showTranscriptMarkersState === true ? <ChatCircleSlashIcon size={20} /> : <ChatCircleIcon size={20} />}
                onSelect={handleToggleTranscriptMarkers}
                shortcut="M"
              >
                {showTranscriptMarkersState === true ? t('media.hideMarkers') : t('media.showMarkers')}
              </MenuItem>
            </MenuSection>
          ) : null}
          {(mediaItem.mediaType === 'video' || mediaItem.mediaType === 'audio') && isEditing ? (
            <MenuSection title={t('media.timeIndexes')}>
              {mediaItem.mediaType === 'video' && isEditing ? (
                <MenuItem
                  icon={<CameraIcon size={20} />}
                  onSelect={handleSetStillFrame}
                  description={t('media.setStillFrameHelp')}
                >
                  {t('media.setStillFrame')}
                </MenuItem>
              ) : null}
              {mediaItem.mediaType === 'video' && isEditing && hasStillFrameImage ? (
                <MenuItem
                  icon={<CameraSlashIcon size={20} />}
                  onSelect={handleClearStillFrame}
                  description={t('media.clearStillFrameHelp')}
                >
                  {t('media.clearStillFrame')}
                </MenuItem>
              ) : null}
              {(mediaItem.mediaType === 'video' || mediaItem.mediaType === 'audio') && isEditing ? (
                <MenuItem
                  icon={<FilmReelIcon size={20} />}
                  onSelect={handleEditModeToggle}
                  description={
                    isEditingMediaContext
                      ? t('media.disableEditingHelp')
                      : t('media.enableEditingHelp')
                  }
                  shortcut="E"
                >
                  {isEditingMediaContext ? t('media.disableEditing') : t('media.enableEditing')}
                </MenuItem>
              ) : null}
            </MenuSection>
          ) : null}
        </>
      }
    >
      <Button
        type="button"
        {...mediaIconButtonProps}
        aria-label={t('media.settings')}
        aria-haspopup="menu"
        onClick={() => setSettingsMenuOpen(!settingsMenuOpen)}
      >
        <GearIcon size={20} />
      </Button>
    </Menu>
  ) : null;

  const saveButton = (
    <Button
      type="button"
      size="sm"
      style="solid"
      color="success"
      background="inverse"
      onClick={handleSave}
    >
      {t('media.save')}
    </Button>
  );

  /**
   * Single row keeps the speed control inside the timestamp box (Figma anatomy);
   * the stacked row owns it instead, so it must never render in both places.
   */
  const inlineSecondaryControls = !stacked && !minimal;

  return (
    <div
      ref={controlsRef}
      data-testid="media-controls"
      aria-disabled={disabled || undefined}
      className={`flex w-full min-w-0 flex-col self-stretch ${stacked ? 'gap-2' : ''} ${compact ? 'px-2 py-1' : 'px-4 pb-4'}`}
    >
      <div className="flex w-full min-w-0 items-center justify-between gap-1.5">
        <div
          data-testid="media-time-display"
          className="flex min-w-0 items-center gap-1 overflow-hidden text-sm text-gray-300 rounded-sm bg-white/5 px-1 py-[2px] border-gray-700 border-[0.5px] border-solid"
        >
          <Button
            type="button"
            {...mediaIconButtonProps}
            data-testid="PlayPauseButton"
            aria-label={isPlaying ? t('media.pause') : t('media.play')}
            className="shrink-0"
            disabled={disabled}
            onClick={(event) => {
              togglePlay();
              recordStat(
                'PlayPauseButtonClicked',
                event,
                statsContext            );
            }}
          >
            {isPlaying ? <PauseIcon weight="fill" size={16} /> : <PlayIcon weight="fill" size={16} />}
          </Button>
          <div className="flex min-w-0 items-center gap-1">
            <span className={`text-white/80 font-light tracking-wider text-right tabular-nums ${isEditingMediaContext ? 'min-w-[72px]' : 'min-w-[35px]'}`}>{currentTime}</span>
            <span className="text-white/20 font-semibold text-lg">/</span>
            <span className="text-white/80 min-w-[35px] font-light tracking-wider tabular-nums">{formatTimeIndex(duration.toString())}</span>
            {inlineSecondaryControls && playbackRateDropdown}
          </div>
        </div>
        {isEditingMediaContext ? saveButton : null}
        {inlineSecondaryControls && !isEditingMediaContext ? (
          <div className="flex shrink-0 items-center justify-end gap-1.5">
            {volumeControl}
            {settingsMenu}
          </div>
        ) : null}
      </div>
      {stacked && !isEditingMediaContext ? (
        <div className="flex w-full min-w-0 flex-wrap items-center justify-end gap-1.5">
          {volumeControl}
          {playbackRateDropdown}
          {settingsMenu}
        </div>
      ) : null}
    </div>
  );
};

export default Controls; 